import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText, CheckCircle, ShieldAlert, UserCheck, CreditCard, Scale, AlertTriangle, Gavel, Server, LogOut, Globe, RefreshCw, Mail } from 'lucide-react';
import { motion, useInView } from 'framer-motion';

const BRAND_NAME = "FinAI";
const CONTACT_EMAIL = "support@finaigpt.com";
const OPERATOR_NAME = "FinAI";

function AnimatedSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function SectionBlock({ icon: Icon, number, title, children }) {
  return (
    <AnimatedSection>
      <div className="group">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-gray-800/60 border border-white/5 flex items-center justify-center group-hover:border-emerald-500/30 transition-colors">
            <Icon className="w-4 h-4 text-emerald-400" />
          </div>
          <h2 className="text-xl md:text-2xl font-semibold text-white">{number}. {title}</h2>
        </div>
        <div className="ml-11">{children}</div>
      </div>
    </AnimatedSection>
  );
}

export default function Terms() {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-emerald-500/8 to-teal-500/5 blur-3xl -top-32 -right-32 animate-pulse" style={{ animationDuration: '5s' }} />
        <div className="absolute w-72 h-72 rounded-full bg-gradient-to-r from-purple-500/5 to-pink-500/5 blur-3xl bottom-1/3 -left-20 animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }} />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-gray-900/80 backdrop-blur-xl border-b border-white/5">
        <div className="h-[2px] bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" className="text-gray-300 hover:text-white" onClick={handleBack}>
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back
          </Button>
          <motion.div 
            className="flex items-center gap-2 text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-full border border-emerald-500/20"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <FileText className="w-4 h-4" />
            <span className="text-xs font-medium">Terms of Service</span>
          </motion.div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-10 md:py-14 max-w-4xl relative z-10">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
            Terms of Service
          </h1>
          <p className="text-gray-400 mb-8 text-sm md:text-base">Please read these terms carefully before using our service</p>
        </motion.div>
        
        <div className="space-y-6">
          {/* Overview Card */}
          <AnimatedSection delay={0.1}>
            <div className="bg-gray-800/40 backdrop-blur-sm rounded-xl p-6 border border-white/5 hover:border-emerald-500/20 transition-colors duration-300">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-white">Agreement Overview</h2>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                These Terms of Service ("Terms") govern your use of {BRAND_NAME} ("Service"), 
                operated by {OPERATOR_NAME}. By accessing or using our Service, you agree to 
                be bound by these Terms.
              </p>
            </div>
          </AnimatedSection>

          <SectionBlock icon={CheckCircle} number={1} title="Acceptance of Terms">
            <p className="text-gray-300 text-sm leading-relaxed">
              By creating an account or using {BRAND_NAME}, you confirm that you are at least 
              18 years old and agree to comply with these Terms. If you do not agree, please 
              do not use our Service.
            </p>
          </SectionBlock>

          <SectionBlock icon={Server} number={2} title="Description of Service">
            <p className="mb-3 text-gray-300 text-sm">{BRAND_NAME} provides:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
              <li><strong className="text-white">Consumer App:</strong> Personal finance tracking, budgeting, investment tracking, and AI-powered financial insights</li>
              <li><strong className="text-white">Advisor Platform:</strong> Professional tools for financial advisors to manage clients and create financial plans (subscription-based)</li>
            </ul>
          </SectionBlock>

          <SectionBlock icon={UserCheck} number={3} title="Account Registration">
            <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
              <li>You must provide accurate and complete information during registration</li>
              <li>You are responsible for maintaining the security of your account credentials</li>
              <li>You must notify us immediately of any unauthorized access</li>
              <li>One person may not maintain multiple accounts</li>
            </ul>
          </SectionBlock>

          <SectionBlock icon={CreditCard} number={4} title="Subscription and Payments (Advisor Platform)">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-2 text-emerald-400 uppercase tracking-wider">Pricing</h3>
                <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
                  <li>Subscription plans are billed monthly</li>
                  <li>Prices are in Indian Rupees (INR) and include applicable taxes</li>
                  <li>We reserve the right to modify pricing with 30 days notice</li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold mb-2 text-emerald-400 uppercase tracking-wider">Free Trial</h3>
                <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
                  <li>New advisor accounts receive a 7-day free trial</li>
                  <li>No payment required during the trial period</li>
                  <li>Trial limitations apply (limited clients and plans)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold mb-2 text-emerald-400 uppercase tracking-wider">Refund Policy</h3>
                <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
                  <li>Subscriptions are non-refundable once activated</li>
                  <li>You may cancel anytime; access continues until the billing period ends</li>
                  <li>Refund requests for exceptional circumstances will be reviewed case-by-case</li>
                </ul>
              </div>
            </div>
          </SectionBlock>

          {/* Disclaimer - Special styling */}
          <AnimatedSection>
            <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-6 hover:border-yellow-500/30 transition-colors duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-yellow-400">5. Financial Advice Disclaimer</h2>
              </div>
              <p className="mb-3 font-medium text-yellow-300 text-sm">
                IMPORTANT: {BRAND_NAME} is NOT a registered investment advisor or financial planner.
              </p>
              <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
                <li>All information provided is for <strong className="text-white">educational and informational purposes only</strong></li>
                <li>AI-generated recommendations are suggestions, not professional advice</li>
                <li>We do not guarantee any financial outcomes or returns</li>
                <li>Always consult a SEBI-registered advisor for investment decisions</li>
                <li>Past performance does not indicate future results</li>
                <li>You are solely responsible for your financial decisions</li>
              </ul>
            </div>
          </AnimatedSection>

          <SectionBlock icon={ShieldAlert} number={6} title="User Responsibilities">
            <p className="mb-3 text-gray-300 text-sm">You agree NOT to:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
              <li>Use the Service for any illegal purpose</li>
              <li>Share your account credentials with others</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Upload malicious code or interfere with the Service</li>
              <li>Scrape or collect data from the platform without permission</li>
              <li>Use the Service to provide unlicensed financial advice to others</li>
            </ul>
          </SectionBlock>

          <SectionBlock icon={Scale} number={7} title="Advisor-Specific Terms">
            <p className="mb-3 text-gray-300 text-sm">If you use the Advisor Platform:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
              <li>You represent that you have appropriate qualifications to provide financial advice</li>
              <li>You are responsible for compliance with applicable regulations (SEBI, AMFI, etc.)</li>
              <li>You are responsible for obtaining client consent before entering their data</li>
              <li>Client data you enter remains your responsibility</li>
              <li>We provide tools; you provide professional judgment</li>
            </ul>
          </SectionBlock>

          <SectionBlock icon={FileText} number={8} title="Intellectual Property">
            <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
              <li>The Service, including all content, features, and functionality, is owned by {OPERATOR_NAME}</li>
              <li>You may not copy, modify, or distribute our content without permission</li>
              <li>You retain ownership of the data you enter into the platform</li>
            </ul>
          </SectionBlock>

          <SectionBlock icon={Gavel} number={9} title="Limitation of Liability">
            <p className="mb-3 text-gray-300 text-sm font-medium">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
              <li>{BRAND_NAME} is provided "AS IS" without warranties of any kind</li>
              <li>We are not liable for any financial losses resulting from use of the Service</li>
              <li>We are not liable for any indirect, incidental, or consequential damages</li>
              <li>Our total liability shall not exceed the amount paid by you in the last 12 months</li>
            </ul>
          </SectionBlock>

          <SectionBlock icon={Globe} number={10} title="Service Availability">
            <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
              <li>We strive for 99.9% uptime but do not guarantee uninterrupted service</li>
              <li>We may perform maintenance with or without notice</li>
              <li>We may modify or discontinue features at our discretion</li>
              <li>We are not liable for any losses due to service unavailability</li>
            </ul>
          </SectionBlock>

          <SectionBlock icon={LogOut} number={11} title="Termination">
            <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
              <li>You may delete your account at any time through account settings</li>
              <li>We may suspend or terminate accounts that violate these Terms</li>
              <li>Upon termination, your right to use the Service ceases immediately</li>
              <li>Data deletion follows our Privacy Policy</li>
            </ul>
          </SectionBlock>

          <SectionBlock icon={Scale} number={12} title="Governing Law and Disputes">
            <ul className="list-disc pl-5 space-y-1.5 text-gray-300 text-sm">
              <li>These Terms are governed by the laws of India</li>
              <li>Any disputes shall be subject to the exclusive jurisdiction of courts in India</li>
              <li>We encourage resolving disputes through direct communication first</li>
            </ul>
          </SectionBlock>

          <SectionBlock icon={RefreshCw} number={13} title="Changes to Terms">
            <p className="text-gray-300 text-sm leading-relaxed">
              We may modify these Terms at any time. Material changes will be notified via email 
              or prominent notice on the Service. Continued use after changes constitutes acceptance 
              of the new Terms.
            </p>
          </SectionBlock>

          {/* Contact Card */}
          <AnimatedSection>
            <div className="bg-gray-800/40 backdrop-blur-sm rounded-xl p-6 border border-white/5 hover:border-emerald-500/20 transition-colors duration-300">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-white">14. Contact Information</h2>
              </div>
              <p className="text-gray-300 text-sm mb-3">For questions about these Terms of Service:</p>
              <div className="space-y-1.5 text-sm">
                <p className="text-gray-300"><strong className="text-white">Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-400 hover:underline">{CONTACT_EMAIL}</a></p>
                <p className="text-gray-300"><strong className="text-white">Operated by:</strong> {OPERATOR_NAME}</p>
              </div>
            </div>
          </AnimatedSection>

          <motion.p 
            className="text-xs text-gray-500 mt-8 pt-4 border-t border-gray-800"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Last updated: {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
          </motion.p>
        </div>
      </div>
    </div>
  );
}
